<template>
  <div class="card listing-card">
    <div v-if="serverError" class="alert alert-danger" role="alert">
      {{serverError}}
    </div>
    <div class="card-body">
      <form @submit.prevent class="needs-validation" novalidate autocomplete="off">
        <div class="label-text">
          <span v-if="isAdd">Add Water Quality Protection Area</span>
          <span v-if="!isAdd">Edit Water Quality Protection Area</span>
        </div>
        <hr>
        <div class="form-group row">
          <label for="featureName" class="col-lg-4 col-form-label label-text">
            <span class="text-danger">*</span>Feature Name
          </label>
          <div class="col-lg-8">
            <input data-vv-as="Feature Name"
              type="text"
              class="form-control"
              v-validate="'required'"
              name="featureName"
              v-model="protectionArea.name"
              placeholder="Enter Feature Name"
              maxlength="50"
            />
            <small v-if="!errors.first('featureName')" class="form-text text-muted">
              Max 50 characters
            </small>
            <div class="invalid-feedback">{{ errors.first('featureName') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="featureType" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Feature Type
          </label>
          <div class="loading col-lg-5" v-if="featureTypesLoading">
            Loading...
          </div>
          <div class="col-lg-8">
            <select v-if="!featureTypesLoading"
              v-validate="'required'"
              data-vv-as="Feature Type"
              class="form-control"
              name="featureType"
              v-model="protectionArea.featureTypeId"
            >
              <option value="" disabled>Select Feature Type</option>
              <option v-for="option in featureTypeOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('featureType') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="geometryType" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Feature Geometry
          </label>
          <div class="loading col-lg-5" v-if="geometryTypesLoading">
            Loading...
          </div>
          <div class="col-lg-8">
            <select v-if="!geometryTypesLoading"
              v-validate="'required'"
              data-vv-as="Feature Type"
              class="form-control"
              name="geometryType"
              @change="handleGeometryTypeUpdate"
              v-model="protectionArea.geometryTypeId"
            >
              <option value="" disabled>Select Feature Geometry</option>
              <option v-for="option in geometryTypeOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
            <small v-if="!errors.first('geometryType') && protectionArea.geometryTypeId === 1" class="form-text text-muted">
              Linear Geometry Type requires feature length, buffer width, and buffer sides
            </small>
            <small v-if="!errors.first('geometryType') && protectionArea.geometryTypeId === 2" class="form-text text-muted">
              Point Geometry Type requires buffer width
            </small>
            <div class="invalid-feedback">{{ errors.first('geometryType') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="featureLength" class="col-lg-4 label-text">
            <span v-if="protectionArea.geometryTypeId === 1" class="text-danger">*</span>Feature Length (ft)
          </label>
          <div class="col-lg-8">
            <input data-vv-as="Feature Length"
              v-validate.disable="{
                required: protectionArea.geometryTypeId === 1, 
                numeric: true
              }"
              class="form-control"
              name="featureLength"
             :disabled="protectionArea.geometryTypeId === 3 || protectionArea.geometryTypeId === 2"
              v-model="protectionArea.featureLength"
              placeholder="Enter Feature Length (ft)"
              maxlength="18"
            />
            <small v-if="!errors.first('featureLength')" class="form-text text-muted">
              Must be a positive whole number or 0
            </small>
            <div class="invalid-feedback">{{ errors.first('featureLength') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="bufferWidth" class="col-lg-4 label-text">
            <span v-if="protectionArea.geometryTypeId === 1 || protectionArea.geometryTypeId === 2" class="text-danger">*</span>Buffer Width (ft)
          </label>
          <div class="col-lg-8">
            <input class="form-control"
              v-model="protectionArea.bufferWidth"
              :disabled="protectionArea.geometryTypeId === 3"
              v-validate.disable="{
                required: protectionArea.geometryTypeId === 1 || protectionArea.geometryTypeId === 2, 
                numeric: true
              }"
              name="bufferWidth"
              data-vv-as="Buffer Width"
              placeholder="Enter Buffer Width (ft)"
              maxlength="18"
            />
            <small v-if="!errors.first('bufferWidth')" class="form-text text-muted">
              Must be a positive whole number or 0
            </small>
            <div class="invalid-feedback">{{ errors.first('bufferWidth') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="bufferRestriction" class="col-lg-4 label-text">
            <span v-if="protectionArea.geometryTypeId === 1" class="text-danger">*</span>Buffer Sides
          </label>
          <div class="loading col-lg-5" v-if="bufferRestrictionsLoading">
            Loading...
          </div>
          <div class="col-lg-8">
            <select v-if="!bufferRestrictionsLoading"
              v-validate.disable="{
                required: protectionArea.geometryTypeId === 1
              }"
              data-vv-as="Buffer Sides"
              :disabled="protectionArea.geometryTypeId === 3 || protectionArea.geometryTypeId === 2"
              class="form-control disabled-select-text"
              name="bufferRestriction"
              v-model="protectionArea.bufferRestrictionId"
            >
              <option value="" disabled>Select Buffer Sides</option>
              <option v-for="option in bufferRestrictionOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('bufferRestriction') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="livestockRestriction" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Livestock Restriction
          </label>
          <div class="loading col-lg-5" v-if="livestockRestrictionsLoading">
            Loading...
          </div>
          <div class="col-lg-8">
            <select v-if="!livestockRestrictionsLoading"
              v-validate="'required'"
              data-vv-as="Livestock Restriction"
              class="form-control"
              name="livestockRestriction"
              v-model="protectionArea.livestockRestrictionId"
            >
              <option value="" disabled>Select Livestock Restriction</option>
              <option v-for="option in livestockRestrictionOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('livestockRestriction') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="timberHarvestRestriction" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Timber Harvest Restriction
          </label>
          <div class="loading col-lg-5" v-if="timberHarvestRestrictionsLoading">
            Loading...
          </div>
          <div class="col-lg-8">
            <select v-if="!timberHarvestRestrictionsLoading"
              v-validate="'required'"
              data-vv-as="Timber Harvest Restriction"
              class="form-control"
              name="timberHarvestRestriction"
              v-model="protectionArea.timberHarvestRestrictionId"
            >
              <option value="" disabled>Select Timber Harvest Restriction</option>
              <option v-for="option in timberHarvestRestrictionOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('timberHarvestRestriction') }}</div>
          </div>
        </div>
      </form>
    </div>
    <div class='card-footer right-align-content'>
      <b-button :disabled="submitting"
        variant="danger"
        class="button-row-spacing"
        v-b-modal="'cancelProtectionAreaConfirmation-' + protectionArea.id"
      >
        Cancel
      </b-button>
      <b-button
        :disabled="submitting"
        @click="submitWrapper(protectionArea)"
      >
        Save
      </b-button>
    </div>
    <CancelModal :modalId="'cancelProtectionAreaConfirmation-' + protectionArea.id"
      @cancelled="cancelEdit"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import CancelModal from '../CancelModal.vue';
import FormSubmit from '../../mixins/FormSubmit';
import { EntityModules, LookupModules } from '@/store/moduleConfig';

export default {
  name: 'ProtectionAreaForm',
  components: {
    CancelModal,
  },
  mixins: [FormSubmit],
  props: [
    'initialProtectionArea',
  ],
  data() {
    return {
      protectionArea: {
        id: this.initialProtectionArea.id,
        globalId: this.initialProtectionArea.globalId,
        instrumentId: this.initialProtectionArea.instrumentId,
        name: this.initialProtectionArea.name,
        featureTypeId: this.initialProtectionArea.featureTypeId,
        geometryTypeId: this.initialProtectionArea.geometryTypeId,
        featureLength: this.initialProtectionArea.featureLength,
        bufferWidth: this.initialProtectionArea.bufferWidth,
        bufferRestrictionId: this.initialProtectionArea.bufferRestrictionId,
        livestockRestrictionId: this.initialProtectionArea.livestockRestrictionId,
        timberHarvestRestrictionId: this.initialProtectionArea.timberHarvestRestrictionId,
      },
    };
  },
  computed: {
    ...mapState(LookupModules.FEATURE_TYPES.namespace, {
      featureTypesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.GEOMETRY_TYPES.namespace, {
      geometryTypesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.BUFFER_RESTRICTIONS.namespace, {
      bufferRestrictionsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.LIVESTOCK_RESTRICTIONS.namespace, {
      livestockRestrictionsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.TIMBER_HARVEST_RESTRICTIONS.namespace, {
      timberHarvestRestrictionsLoading: state => state.loading,
    }),
    ...mapState(EntityModules.WATER_QUALITY_PROTECTION_AREA.namespace, {
      serverError: state => state.serverError,
      submitting: state => state.submitting,
    }),
    ...mapGetters(LookupModules.GEOMETRY_TYPES.namespace, {
      geometryTypeOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.FEATURE_TYPES.namespace, {
      featureTypeOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.BUFFER_RESTRICTIONS.namespace, {
      bufferRestrictionOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.LIVESTOCK_RESTRICTIONS.namespace, {
      livestockRestrictionOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.TIMBER_HARVEST_RESTRICTIONS.namespace, {
      timberHarvestRestrictionOptions: 'getActiveOptions',
    }),
    isAdd() {
      return !this.protectionArea.id;
    },
  },
  methods: {
    cancelEdit() {
      this.$emit('closeForm', this.protectionArea.id);
    },
    handleGeometryTypeUpdate() {
      this.protectionArea.featureLength = null; 
      this.protectionArea.bufferWidth = null; 
      this.protectionArea.bufferRestrictionId = '';

      this.errors.remove('featureLength');
      this.errors.remove('bufferWidth');
      this.errors.remove('bufferRestriction');
    }, 
    submitWrapper(entity) {
      this.$validator.validate()
        .then((valid) => {
          if (valid) {
            this.onSubmit(
              entity,
              EntityModules.WATER_QUALITY_PROTECTION_AREA.namespace,
            );
          }
        });
    },
  },
};
</script>
